import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";

import { api, riderScreenQuery, type Me, type OpsOrder, type QueueOrder } from "./api";

// New-order alert for pharmacy staff and ops (and a new-run alert for riders), on every page of the panel:
// a loud chime that repeats every 20 seconds until someone opens each new
// order, the tab title flashing, and a system notification when the tab is
// in the background. Browsers only allow sound after one click on the page,
// so a banner asks for that click once.

const SEEN_KEY = "savon-seen-orders";
const REPEAT_MS = 20_000;

function readSeen(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

/** Marks an order as looked at, which stops its alert. Called when its card opens. */
export function markOrderSeen(orderId: string) {
  const seen = readSeen();
  if (seen.has(orderId)) return;
  seen.add(orderId);
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-500)));
  } catch {
    // Private mode: the alert simply stops once the order is no longer new.
  }
  window.dispatchEvent(new Event("savon-seen"));
}

let audio: AudioContext | null = null;

function unlockSound(): boolean {
  try {
    audio ??= new AudioContext();
    void audio.resume();
    return audio.state !== "suspended";
  } catch {
    return false;
  }
}

/** A rising three-note chime, played twice: loud enough to hear across a counter. */
function chime() {
  if (!audio || audio.state === "suspended") return;
  const notes = [784, 988, 1319];
  for (let round = 0; round < 2; round++) {
    notes.forEach((freq, i) => {
      const at = audio!.currentTime + round * 0.9 + i * 0.22;
      const osc = audio!.createOscillator();
      const gain = audio!.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.6, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.35);
      osc.connect(gain).connect(audio!.destination);
      osc.start(at);
      osc.stop(at + 0.4);
    });
  }
}

export function NewOrderAlerts({ me }: { me: Me }) {
  const [, navigate] = useLocation();
  const [soundOn, setSoundOn] = useState(false);
  const [seenTick, setSeenTick] = useState(0);
  const baseTitle = useRef(document.title);

  // Pharmacy staff hear their own pharmacy's new orders; ops hears every pharmacy's.
  const placed = useQuery({
    queryKey: ["alerts", me.role],
    queryFn: async () =>
      me.role === "ops"
        ? (await api<{ orders: OpsOrder[] }>("/ops/orders?status=placed")).orders.map((o) => o.orderId)
        : (await api<{ orders: QueueOrder[] }>("/pharmacy/orders?status=placed")).orders.map((o) => o.orderId),
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    enabled: me.role === "ops" || me.role === "pharmacy",
  });
  // A rider hears a run assigned to them, until they open it or pick the bags up.
  // Same query as the rider screen, so it is one request.
  const riderRun = useQuery({ ...riderScreenQuery, refetchInterval: 15_000, refetchIntervalInBackground: true, enabled: me.role === "rider" });
  const rider = me.role === "rider";
  const assignedRun = rider && riderRun.data?.run?.status === "assigned" ? `run-${riderRun.data.run.runId}` : null;

  useEffect(() => {
    const onSeen = () => setSeenTick((n) => n + 1);
    window.addEventListener("savon-seen", onSeen);
    window.addEventListener("storage", onSeen);
    return () => {
      window.removeEventListener("savon-seen", onSeen);
      window.removeEventListener("storage", onSeen);
    };
  }, []);

  const seen = readSeen();
  void seenTick;
  const fresh = (rider ? (assignedRun ? [assignedRun] : []) : (placed.data ?? [])).filter((id) => !seen.has(id));

  // Ring now and every 20 s while there are unopened new orders.
  const notified = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (fresh.length === 0) {
      document.title = baseTitle.current;
      return;
    }
    const ring = () => {
      chime();
      const flash = rider ? "🔔 নতুন রান" : `🔔 ${fresh.length} new order${fresh.length > 1 ? "s" : ""}`;
      document.title = document.title.startsWith("🔔") ? baseTitle.current : flash;
    };
    ring();
    const brandNew = fresh.filter((id) => !notified.current.has(id));
    if (brandNew.length && document.hidden && "Notification" in window && Notification.permission === "granted") {
      if (rider) new Notification("SAVON: নতুন রান", { body: "ফার্মেসি থেকে ব্যাগ নিন", tag: "savon-new-run", requireInteraction: true });
      else new Notification("SAVON: new order", { body: brandNew.join(", "), tag: "savon-new-order", requireInteraction: true });
    }
    brandNew.forEach((id) => notified.current.add(id));
    const timer = setInterval(ring, REPEAT_MS);
    return () => clearInterval(timer);
  }, [fresh.join(",")]);

  return (
    <div className="no-print">
      {!soundOn && (
        <div className="flex flex-wrap items-center gap-3 border-b bg-secondary px-4 py-2 text-sm">
          <span className="flex-1">{rider ? "নতুন রান এলে শব্দ শুনতে এটি চালু করুন।" : "Turn on the new-order sound for this screen."}</span>
          <Button
            size="sm"
            onClick={() => {
              setSoundOn(unlockSound());
              chime();
              if ("Notification" in window && Notification.permission === "default") void Notification.requestPermission();
            }}
          >
            {rider ? "শব্দ চালু করুন" : "Turn on sound"}
          </Button>
        </div>
      )}
      {rider && fresh.length > 0 && (
        <button className="flex min-h-[56px] w-full items-center gap-3 bg-destructive px-4 py-3 text-left text-lg font-semibold text-destructive-foreground" onClick={() => markOrderSeen(fresh[0]!)}>
          🔔 নতুন রান এসেছে: ফার্মেসি থেকে ব্যাগ নিন
          <span className="ml-auto underline">দেখেছি</span>
        </button>
      )}
      {!rider && fresh.length > 0 && (
        <button
          className="flex w-full items-center gap-3 bg-destructive px-4 py-3 text-left font-semibold text-destructive-foreground"
          onClick={() => navigate(`/queue/${fresh[0]}`)}
        >
          🔔 {fresh.length} new order{fresh.length > 1 ? "s" : ""}: {fresh.join(", ")}
          <span className="ml-auto underline">Open</span>
        </button>
      )}
    </div>
  );
}
